<script setup>
import UserInfo from './components/UserInfo'
import AccumulativeProjectInfo from './components/AccumulativeProjectInfo';
import EmployeesWithoutClockify from './components/EmployeesWithoutClockify';
import ReportService from '@/api/services/report.dailyproduction.service';
import ResourceService from '@/api/services/resource.service';
import OccupationProgress from './components/OccupationProgress'
import _ from 'lodash'
import moment from 'moment'
import EmployeesWithAbsences from "./components/EmployeesWithAbsences";
import EmployeesWithHolidays from "./components/EmployeesWithHolidays";
import Notification from '@/utils/plugins/NotificationService';
import { filterByNormalizedText } from '@/utils/textSearch';
import { computed, onMounted, ref } from "vue";
import { useUiStore } from "@/app/stores/uiStore";
import { toHours } from "@/views/production/mixin/common";

const projectDialogVisible = ref(false);
const employeesWithoutClockifyDialogVisible = ref(false);
const holidaysDialogVisible = ref(false);
const info = ref(null);
const absencesDialogVisible = ref(false);
const departments = ref(null);
// eslint-disable-next-line no-unused-vars
const department = ref(null);
const selectedDepartmentFilter = ref(null);
const personSearch = ref('');
const params = ref({});
// eslint-disable-next-line no-unused-vars
const numEmployees = ref(0);

const now = new Date();
const selectedDatePreset = ref('today');
const selectedDateRange = ref([]);
const datePresetOptions = [
  { label: 'Hoy', value: 'today' },
  { label: 'Ayer', value: 'yesterday' },
  { label: 'Esta semana', value: 'this_week' },
  { label: 'Semana pasada', value: 'last_week' },
  { label: 'Este mes', value: 'this_month' },
  { label: 'El mes pasado', value: 'last_month' },
  { label: 'Personalizado', value: 'custom' },
];
const datePickerOptions = {
  firstDayOfWeek: 1,
};
const datePickerShortcuts = [
  { text: 'Hoy', value: () => getPresetRange('today') },
  { text: 'Ayer', value: () => getPresetRange('yesterday') },
  { text: 'Esta semana', value: () => getPresetRange('this_week') },
  { text: 'Semana pasada', value: () => getPresetRange('last_week') },
  { text: 'Este mes', value: () => getPresetRange('this_month') },
  { text: 'El mes pasado', value: () => getPresetRange('last_month') },
];

const uiStore = useUiStore();

onMounted(() => {
  loadInfo();
})

const occupationPercent = computed(() => {
  const val = parseFloat((info.value.workedInProjects * 100 / info.value.workedInSignins).toFixed(2))
  if (isNaN(val)) {
    return 0;
  }
  return val > 100 ? 100 : val;
})
const userData = computed(() => {
  let array = _.toArray(info.value.employees);
  const newArray = array.filter(element => canBeSeen(element))
  return newArray;
})
const filteredUserData = computed(() => filterByNormalizedText(
  userData.value,
  personSearch.value.trim(),
  user => [user.employee?.name, user.employee?.lastname].filter(Boolean).join(' '),
));
const parseAbsenceDate = (value, endOfDayForDateOnly = false) => {
  if (!value) return null;

  const date = moment(value, [
    'YYYY-MM-DD',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DD HH:mm:ss',
    'DD-MM-YYYY',
    'DD-MM-YYYY HH:mm',
    'DD-MM-YYYY HH:mm:ss',
    'DD/MM/YYYY',
    'DD/MM/YYYY HH:mm',
    'DD/MM/YYYY HH:mm:ss',
    moment.ISO_8601,
  ], true);
  if (!date.isValid()) return null;

  const hasTime = /(?:T|\s)\d{1,2}:\d{2}/.test(String(value));
  return endOfDayForDateOnly && !hasTime ? date.endOf('day') : date;
}
const isActiveAbsence = (absence) => {
  if (!absence?.fromDate) return false;

  const [, selectedEndDate] = selectedDateRange.value ?? [];
  const selectedDate = moment(selectedEndDate || new Date());
  const referenceDate = selectedDate.isSame(moment(), 'day')
    ? moment()
    : selectedDate.endOf('day');
  const absenceStartDate = parseAbsenceDate(absence.fromDate);
  const absenceEndDate = parseAbsenceDate(absence.toDate, true);

  if (!absenceStartDate || absenceStartDate.isAfter(referenceDate)) return false;
  return !absenceEndDate || !absenceEndDate.isBefore(referenceDate);
}
const countNumEmployeesWorking = computed(() => {
  let employeeWithHolidays = 0;
  let employeeWithAbsence = 0;
  let employeeWithPublicHolidays = 0;
  info.value.employees.forEach(employee => {
    if (employee.holidays.length !== 0) {
      employeeWithHolidays += 1;
    }
    if (employee.publicHoliday.length !== 0) {
      employeeWithPublicHolidays += 1;
    }
    if (isActiveAbsence(employee.absence)) {
      employeeWithAbsence += 1;
    }

  })

  return info.value.employees.length - employeeWithHolidays - employeeWithAbsence - employeeWithPublicHolidays;
})
const isSingleDayRange = computed(() => {
  const [fromDate, toDate] = selectedDateRange.value ?? [];
  if (!fromDate || !toDate) {
    return true;
  }

  return startOfDay(fromDate).getTime() === startOfDay(toDate).getTime();
})


const loadInfo = async () => {
  applyDatePreset(selectedDatePreset.value);
  await getDepartments();
}
const getDailyReport = () => {
  const [fromDate, toDate] = selectedDateRange.value ?? [];
  if (!fromDate || !toDate) {
    return;
  }

  params.value.fromDate = formatDate(startOfDay(fromDate));
  params.value.toDate = formatDate(endOfDay(toDate));
  delete params.value.date;

  uiStore.startLoading();
  ReportService.getDailyReport(params.value)
    .then(result => {
      info.value = result.data;
      // eslint-disable-next-line no-unused-vars
      const temp = info.value.employeesWithoutClockify.filter(emp => emp.canCreateTimeEntry);
      info.value.employeesWithoutClockify = info.value.employeesWithoutClockify.filter(emp => !emp.canCreateTimeEntry);
    })
    .catch(() => {
      Notification.addNotification('Error al obtener datos de Produccion.', 'error', 6000);
      uiStore.stopLoading();
    }).finally(
      () => {
        uiStore.stopLoading();
      }
    )
}

const getDepartments = () => {
  uiStore.startLoading();
  params.value = {
    'jobs.productive': true
  }
  ResourceService.getDepartments(params.value)
    .then(result => {
      departments.value = result.data['hydra:member'];
      for (let i = 0; i < departments.value.length; i++) {
        const element = departments.value[i];
        if (element.name == "Desarrollo") {
          selectedDepartmentFilter.value = element.id;
        }
        if (element.name === "Externo") {
          departments.value[i].name = "Colaboradores externos"
        }
      }
      filterByDepartment();
      uiStore.stopLoading();
    })
    .catch(() => {
      Notification.addNotification('Error obtener listado de Departamentos.', 'error', 6000);
      uiStore.stopLoading();
    })
}
const clockifySynced = () => {
  employeesWithoutClockifyDialogVisible.value = false;
  loadInfo();

}
const size = (object) => {
  return _.size(object);
}
const filterByDepartment = () => {
  if (!selectedDepartmentFilter.value) {
    delete params.value.department;
  } else {
    params.value.department = selectedDepartmentFilter.value
  }
  getDailyReport();
}

const handleDatePresetChange = (preset) => {
  applyDatePreset(preset);
  getDailyReport();
}

const manejarCambioFecha = (val) => {
  if (!val || val.length !== 2) {
    applyDatePreset(selectedDatePreset.value === 'custom' ? 'today' : selectedDatePreset.value);
    if (selectedDatePreset.value === 'custom') {
      selectedDatePreset.value = 'today';
    }
  } else {
    selectedDatePreset.value = detectPresetFromRange(val) ?? 'custom';
  }
  getDailyReport();
}

const applyDatePreset = (preset) => {
  selectedDateRange.value = getPresetRange(preset);
}

const getPresetRange = (preset) => {
  if (preset === 'yesterday') {
    const yesterday = addDays(startOfDay(now), -1);
    return [yesterday, endOfDay(yesterday)];
  }

  if (preset === 'this_week') {
    const today = new Date(now);
    const day = today.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const start = addDays(startOfDay(today), diffToMonday);
    return [start, endOfDay(now)];
  }

  if (preset === 'last_week') {
    const today = new Date(now);
    const day = today.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const startOfThisWeek = addDays(startOfDay(today), diffToMonday);
    const start = addDays(startOfThisWeek, -7);
    const end = addDays(start, 6);
    return [start, endOfDay(end)];
  }

  if (preset === 'this_month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    return [start, endOfDay(now)];
  }

  if (preset === 'last_month') {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    return [start, end];
  }

  if (preset === 'custom') {
    return selectedDateRange.value?.length === 2 ? selectedDateRange.value : [startOfDay(now), endOfDay(now)];
  }

  return [startOfDay(now), endOfDay(now)];
}

const detectPresetFromRange = (range) => {
  const normalizedRange = normalizeRange(range);
  return datePresetOptions
    .filter(option => option.value !== 'custom')
    .find(option => compareRanges(normalizedRange, normalizeRange(getPresetRange(option.value))))
    ?.value ?? null;
}

const normalizeRange = (range) => {
  return [
    formatDate(startOfDay(range[0])),
    formatDate(endOfDay(range[1])),
  ];
}

const compareRanges = (firstRange, secondRange) => {
  return firstRange[0] === secondRange[0] && firstRange[1] === secondRange[1];
}

const startOfDay = (date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate;
}

const endOfDay = (date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(23, 59, 59, 999);
  return normalizedDate;
}

const addDays = (date, days) => {
  const normalizedDate = new Date(date);
  normalizedDate.setDate(normalizedDate.getDate() + days);
  return normalizedDate;
}

const formatDate = (date) => {
  const offset = new Date(date).getTimezoneOffset();
  const sign = offset > 0 ? '-' : '+';
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const minutes = String(Math.abs(offset) % 60).padStart(2, '0');

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  const hoursOfDay = String(date.getHours()).padStart(2, '0');
  const minutesOfDay = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hoursOfDay}:${minutesOfDay}:${seconds}${sign}${hours}:${minutes}`;
}

const canBeSeen = (user) => {
  if (user.employee.employeeType === "external_collaborator") {
    if (user.totalWorked >= 1) {
      return true;
    } else {
      return false;
    }
  }
  return true;
}

</script>

<template>
  <div>
    <el-container v-if="info">
      <el-main>
        <el-row class="production-summary-row" :gutter="10">
          <el-col :lg="8" :md="12" :sm="24" :xs="24" class="pb-2 production-summary-col">
            <el-card class="production-dashboard-card">
              <div class="production-dashboard-card-content">
                <div class="dashboard-icon employee">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 45" fill="none">
                    <path
                      d="M49.9805 16.4082C47.0812 19.4103 44.182 22.4123 41.3022 25.434C41.1558 25.5812 41.107 25.8755 41.107 26.0913C41.0972 31.8992 41.0972 37.6973 41.0972 43.5051C41.0972 43.7014 41.0972 43.8976 41.0972 44.133C30.369 44.133 19.6603 44.133 8.89301 44.133C8.89301 43.9368 8.89301 43.7504 8.89301 43.5542C8.89301 37.7365 8.89301 31.909 8.90277 26.0913C8.90277 25.7578 8.80515 25.5125 8.57087 25.277C5.7009 22.324 2.85045 19.3612 0 16.4082C0 16.3788 0 16.3396 0 16.3101C0.10738 16.1728 0.224522 16.0354 0.32214 15.8883C2.42093 12.641 4.49043 9.37404 6.62827 6.15616C9.26396 2.17305 13.0125 0.0932008 17.7763 0.0441477C22.5888 -0.0147159 27.4014 -0.0147159 32.2237 0.0441477C36.9875 0.103011 40.736 2.17305 43.3717 6.16597C45.4998 9.39366 47.5791 12.6508 49.6779 15.8981C49.7755 16.0453 49.8926 16.1826 50 16.3199C49.9805 16.3396 49.9805 16.369 49.9805 16.4082ZM16.1656 3.08544C13.1687 3.61521 10.8161 5.11623 9.14682 7.63756C7.39945 10.2962 5.69114 12.9843 3.95353 15.6528C3.7583 15.9471 3.78758 16.1041 4.02187 16.3494C5.54471 17.9093 7.05779 19.4986 8.57087 21.0683C8.6392 21.1468 8.72706 21.2154 8.88325 21.3626C8.88325 19.5771 8.88325 17.9093 8.88325 16.2415C9.88872 16.2415 10.8356 16.2415 11.8216 16.2415C11.8216 24.5805 11.8216 32.8803 11.8216 41.1997C20.6072 41.1997 29.3538 41.1997 38.1589 41.1997C38.1589 32.8606 38.1589 24.5511 38.1589 16.2415C39.1644 16.2415 40.1113 16.2415 41.107 16.2415C41.107 17.9387 41.107 19.5967 41.107 21.2939C41.1948 21.2351 41.2339 21.2253 41.2632 21.186C42.8446 19.5378 44.426 17.8896 46.0172 16.2513C46.2124 16.0551 46.1831 15.9079 46.0465 15.7019C45.5974 15.0249 45.1581 14.3284 44.7091 13.6515C43.2058 11.3558 41.7903 9.00123 40.1699 6.79385C38.6177 4.67476 36.392 3.5171 33.7661 3.06582C33.6294 3.74275 33.5025 4.40006 33.3561 5.04756C32.4776 8.80502 29.2366 11.5618 25.4783 11.758C21.5931 11.964 18.0789 9.58987 16.8391 5.87165C16.556 4.99851 16.3998 4.0665 16.1656 3.08544ZM30.779 2.98733C26.9036 2.98733 23.0574 2.98733 19.2112 2.98733C19.5139 6.43085 21.8665 8.85407 24.9707 8.83445C27.8993 8.81483 30.408 6.80366 30.779 2.98733Z"
                      fill="white" />
                    <path
                      d="M31.9796 14.4657V24.4431C31.9796 25.4143 31.8137 26.3169 31.472 27.1606C31.1304 27.9945 30.6227 28.7598 29.9394 29.4367C29.2561 30.1234 28.4947 30.6434 27.6649 30.9868C26.8352 31.3399 25.9273 31.5067 24.9511 31.5165C23.9847 31.5165 23.0964 31.3399 22.2666 30.9868C21.4369 30.6336 20.6852 30.1234 19.9921 29.4367C19.3088 28.7598 18.8012 28.0043 18.4595 27.1606C18.1179 26.3267 17.9519 25.4143 17.9519 24.4431V14.4657H23.0378V24.4431C23.0476 25.0023 23.2428 25.4634 23.604 25.8166C23.9652 26.1796 24.424 26.366 24.9609 26.3758C25.5173 26.366 25.9761 26.1796 26.3471 25.8166C26.718 25.4536 26.9035 24.9925 26.9132 24.4431V17.4089L31.9796 14.4657Z"
                      fill="white" />
                  </svg>
                </div>
                <div class="dashboard-text">
                  <h2>
                    {{ countNumEmployeesWorking }} Empleados
                    <el-button v-if="info.employeesWithoutClockify.length > 0"
                      @click="employeesWithoutClockifyDialogVisible = true">
                      {{ info.employeesWithoutClockify.length }} sin id de clockify
                    </el-button>
                  </h2>
                  <occupation-progress :percentaje="occupationPercent" :isFulled="occupationPercent >= 100" />
                </div>
              </div>
            </el-card>
          </el-col>

          <el-col :lg="8" :md="12" :sm="24" :xs="24" class="pb-2 production-summary-col">
            <el-card class="production-dashboard-card">
              <div class="production-dashboard-card-content">
                <div class="dashboard-icon project">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 45" fill="none">
                    <path
                      d="M18.04 45C12.43 31.9 6.79 18.82 1.28 5.69C0.54 3.92 0.41 1.9 0 0H2C14.75 5.65 27.49 11.29 40.23 16.95C41.69 17.6 43.09 18.36 44.52 19.06C44.58 19.54 44.64 20.02 44.71 20.5C41.16 21.99 37.69 23.73 34.03 24.91C29.1 26.49 25.71 29.12 24.32 34.4C23.36 38.06 21.49 41.48 20.03 45H18.03H18.04ZM18.97 39.96C21.43 34.45 22.43 29.14 25.59 25.98C28.72 22.86 34.03 21.91 39.27 19.61C27.24 14.44 16.09 9.65 3.72 4.33C9.02 16.72 13.79 27.85 18.97 39.96Z"
                      fill="white" />
                  </svg>
                </div>
                <div class="dashboard-text">
                  <h2>
                    {{ size(info.projects) }} proyectos
                    <el-button @click="projectDialogVisible = true">
                      ( <font-awesome-icon icon="thumbs-up" />
                      {{ toHours(info.workedInSignins) }} /
                      <font-awesome-icon icon="clock" />
                      {{ toHours(info.workedInProjects) }})
                    </el-button>
                  </h2>
                  <small> Trabajando en {{ size(info.currentProjects) }} proyectos en estos momentos
                    ({{ toHours(info.workedCurrentInProjects) }}) </small>
                </div>
              </div>
            </el-card>
          </el-col>

          <el-col :lg="8" :md="12" :sm="24" :xs="24" class="pb-2 production-summary-col">
            <el-card class="production-dashboard-card">
              <div class="production-dashboard-card-content">
                <div class="dashboard-icon holidays-absences">
                  <svg id="islands" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 372 372" fill="white">
                    <path
                      d="M186 0C83.4 0 0 83.4 0 186s83.4 186 186 186 186-83.4 186-186S288.6 0 186 0zM186 206.6c36.4 0 66.7 27.1 71.5 62.2H114.5C119.3 233.7 149.6 206.6 186 206.6zM266.3 233.4c-2.1-3.6-4.4-7.1-6.9-10.4h88.4c-0.8 3.4-1.7 6.8-2.7 10.2h-76.9C267.6 233.3 266.9 233.3 266.3 233.4zM186 20c91.5 0 166 74.5 166 166 0 5.8-0.3 11.4-0.9 17h-112.6C223.6 192.7 205.5 186.6 186 186.6c-4.4 0-8.7 0.3-12.9 0.9 -7.9-22.4-2.4-47 14.1-63.9 3.3 0.6 6.4 2.2 9 4.6 3.5 3.3 5.4 7.8 5.5 12.5 0.1 5.4 4.6 9.8 10 9.8 0.1 0 0.2 0 0.2 0 5.5-0.1 9.9-4.7 9.8-10.2 -0.2-10.1-4.4-19.5-11.7-26.5 -2.3-2.2-4.8-4.1-7.6-5.6 10.5-8.4 25.6-9.3 37.2-1.3 4.5 3.1 10.8 2 13.9-2.5 3.1-4.5 2-10.8-2.5-13.9 -19.1-13.2-44.2-11.6-61.4 2.4 -8.8-21.9-31.4-36.1-55.8-33.1 -5.5 0.7-9.4 5.6-8.7 11.1 0.7 5.5 5.6 9.4 11.1 8.7 13-1.6 25.1 4.6 31.8 14.9 -12.4-2.6-25.9 1-35.3 10.9 -3.8 4-3.7 10.3 0.3 14.1 1.9 1.8 4.4 2.8 6.9 2.8 2.6 0 5.3-1 7.2-3.1 5.5-5.8 13.9-7 20.8-3.8 -7.5 9.3-13 20.3-15.9 32 -3.7 14.9-3 30.7 1.7 45.2 -7.2 2.7-14 6.3-20.2 10.6H20.9C20.3 197.4 20 191.8 20 186 20 94.5 94.5 20 186 20zM186 352c-67.6 0-125.9-40.6-151.8-98.7h33.6c5.5 0 10-4.5 10-10s-4.5-10-10-10H26.9c-1-3.4-1.9-6.8-2.7-10.2h88.4c-11.8 15.5-18.8 34.8-18.8 55.8 0 5.5 4.5 10 10 10h164.5c5.5 0 10-4.5 10-10 0-8.9-1.3-17.4-3.6-25.5h63.1C311.9 311.4 253.6 352 186 352z" />
                  </svg>
                </div>
                <div class="dashboard-text">
                  <h6 class="pointer h6-text" @click="holidaysDialogVisible = true">
                    {{
                      size(info.holidays.holidaysOn7Days) }} empleado{{
                      size(info.holidays.holidaysOn7Days) == 0 || size(info.holidays.holidaysOn7Days)
                        > 1 ? 's' : '' }} estará{{ size(info.holidays.holidaysOn7Days) == 0 ||
                      size(info.holidays.holidaysOn7Days) > 1 ? 'n' : '' }} de vacaciones en los
                    próximos 7 días
                  </h6>
                  <h6 class="pointer h6-text" @click="holidaysDialogVisible = true">
                    {{
                      size(info.holidays.holidaysToday) }} empleado{{
                      size(info.holidays.holidaysOn7Days) == 0 || size(info.holidays.holidaysOn7Days)
                        > 1 ? 's' : '' }} está{{ size(info.holidays.holidaysOn7Days) == 0 ||
                      size(info.holidays.holidaysOn7Days) > 1 ? 'n' : '' }} de vacaciones
                  </h6>
                  <h6 class="pointer h6-text" @click="absencesDialogVisible = true">
                    {{ size(info.absences) }} empleado{{ size(info.absences) == 0 ||
                      size(info.absences) > 1 ? 's' : '' }} ausente{{ size(info.absences) == 0 ||
                      size(info.absences) > 1 ? 's' : '' }}
                  </h6>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <el-divider class="my-2" />

        <div class="col-md-12">
          <div class="row production-filters-row">
            <div class="col-12 production-department-filter">
              <div class="form-item mb-1">
                <label class="label-state">Departamentos:</label>
                <el-select clearable class="w-100" v-model="selectedDepartmentFilter" placeholder="TODOS"
                  @change="filterByDepartment()">
                  <el-option v-for="department in departments" :key="department.id" :label="department.name"
                    :value="department.id" />
                </el-select>
              </div>
            </div>
            <div class="col-12 production-period-filter">
              <div class="form-item mb-1">
                <label class="label-state">Periodo:</label>
                <el-select class="w-100" v-model="selectedDatePreset" @change="handleDatePresetChange">
                  <el-option v-for="option in datePresetOptions" :key="option.value" :label="option.label"
                    :value="option.value" />
                </el-select>
              </div>
            </div>
            <div class="col-12 production-person-search-actions"
              :class="{ 'production-person-search-with-dates': selectedDatePreset === 'custom' }">
              <div class="form-item mb-1 production-person-filter">
                <label for="production-person-search" class="label-state">Buscar persona:</label>
                <el-input id="production-person-search" v-model="personSearch" clearable class="w-100"
                  placeholder="Buscar por nombre o apellidos" />
              </div>
              <div class="production-date-refresh-controls">
                <div v-if="selectedDatePreset === 'custom'" class="production-custom-date-filter">
                  <div class="form-item date-picker mb-1">
                    <label>*Fechas:</label>
                    <el-date-picker v-model="selectedDateRange" type="daterange" placeholder="Selecciona el periodo"
                      start-placeholder="Fecha inicio" end-placeholder="Fecha fin" range-separator="-" unlink-panels
                      format="DD/MM/YYYY" class="w-100 production-date-range" :picker-options="datePickerOptions"
                      :shortcuts="datePickerShortcuts" @change="manejarCambioFecha" />
                  </div>
                </div>
                <div class="production-refresh-filter">
                  <div class="form-item mb-1">
                    <label class="label-state production-refresh-label" aria-hidden="true">&nbsp;</label>
                    <button type="button" class="production-refresh-button" aria-label="Actualizar"
                      title="Actualizar" @click="filterByDepartment">
                      <img src="@/assets/icons/refresh-icon.png" alt="" class="refresh-icon" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <el-divider class="my-2" />

        <el-empty v-if="personSearch.trim() && filteredUserData.length === 0"
          description="No hay personas que coincidan con la búsqueda." />

        <el-row v-else class="pt-2 production-users-row" :gutter="10">
          <el-col v-for="user in filteredUserData" :key="user.id" :xl="6" :lg="6" :md="12" :sm="12" :xs="24"
            class="production-user-col">
            <user-info :info="user" :is-single-day="isSingleDayRange"
              :is-absence-active="isActiveAbsence(user.absence)" class="mb-3" />
          </el-col>
        </el-row>
      </el-main>
      <el-dialog v-model="projectDialogVisible" title="Proyectos" class="el-dialog-production-dashboard">
        <accumulative-project-info :projects="info.projects" :current-projects="info.currentProjects" />
      </el-dialog>
      <el-dialog v-model="employeesWithoutClockifyDialogVisible" title="Empleados sin sincronizar con clockify"
        class="el-dialog-production-dashboard">
        <employees-without-clockify :employees-without-clockify="info.employeesWithoutClockify"
          @clockify-synced="clockifySynced" />
      </el-dialog>
      <el-dialog v-model="holidaysDialogVisible" title="Empleados de Vacaciones" class="el-dialog-production-dashboard">
        <employees-with-holidays :holidays="info.holidays" />
      </el-dialog>
      <el-dialog v-model="absencesDialogVisible" title="Empleados ausentes" class="el-dialog-production-dashboard">
        <employees-with-absences :absences="info.absences" />
      </el-dialog>
    </el-container>
  </div>
</template>

<style lang="scss">
.refresh-col {
  display: flex;
  justify-content: center !important;
  align-items: center !important;
  padding-top: 10px;
}

.refresh-col-wrapper {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex: 0 0 auto;
  width: auto;
  padding-left: 8px;
}

.refresh-action {
  display: flex;
  flex-direction: column;
}

.refresh-label-placeholder {
  visibility: hidden;
  margin-bottom: 0.5rem;
}

.refresh-icon {
  cursor: pointer;
  width: 35px;
  height: 35px;
  margin-top: -4px;
  background: #F7D20E;
  border-radius: 50%;
  padding: 5px;
  padding-right: 3px;
}

.el-dropdown-link {
  cursor: pointer;
  color: #409EFF;
}

.el-icon-arrow-down {
  font-size: 12px;
}

.production-filters-row {
  align-items: flex-start;
}

.production-person-search-actions {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.production-person-search-with-dates {
  flex-wrap: wrap;
}

.production-date-refresh-controls {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 0 0 44px;
  min-width: 0;
}

.production-person-search-with-dates .production-date-refresh-controls {
  flex: 1 1 300px;
  max-width: 424px;
}

.production-custom-date-filter {
  flex: 1 1 0;
  min-width: 0;
  container: production-dates / inline-size;
}

.production-custom-date-filter .production-date-range {
  min-width: 0;
}

.production-refresh-filter {
  display: flex;
  align-items: flex-end;
  flex: 0 0 44px;
  width: 44px;
}

.production-refresh-label {
  visibility: hidden;
}

.production-person-filter {
  flex: 1 1 280px;
  min-width: 0;
  max-width: 280px;
}

.production-person-filter .el-input {
  height: 44px;
}

.production-refresh-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;
  height: 44px;
  padding: 0;
  border: none;
  background: transparent;
}

.production-refresh-button .refresh-icon {
  margin-top: 0;
}

.production-refresh-button:focus-visible {
  outline: 2px solid #409eff;
  outline-offset: 2px;
  border-radius: 50%;
}

@media (min-width: 768px) {
  .production-filters-row,
  .production-filters-row > .production-person-search-with-dates {
    flex-wrap: nowrap;
  }

  .production-filters-row label {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .production-filters-row > .production-department-filter,
  .production-filters-row > .production-period-filter {
    flex: 1 1 160px;
    width: 160px;
    max-width: 320px;
    min-width: 0;
  }

  .production-filters-row > .production-person-search-actions {
    flex: 0 1 360px;
    width: 360px;
    max-width: 360px;
  }

  .production-filters-row > .production-person-search-with-dates {
    flex: 1 1 660px;
    width: 660px;
    max-width: 740px;
  }
}

@container production-dates (max-width: 220px) {
  .production-date-range {
    padding-inline: 4px;
  }

  .production-date-range .el-range-input {
    font-size: 12px;
    min-width: 0;
  }

  .production-date-range .el-range-separator {
    flex: 0 0 12px;
    padding: 0;
  }
}

.production-date-range.el-date-editor,
.production-date-range.el-input__wrapper,
.production-date-range.el-range-editor.el-input__wrapper,
.production-date-range.el-date-editor:hover,
.production-date-range.el-input__wrapper:hover,
.production-date-range.el-range-editor.el-input__wrapper:hover,
.production-date-range.el-date-editor.is-active,
.production-date-range.el-input__wrapper.is-focus,
.production-date-range.el-range-editor.el-input__wrapper.is-focus {
  border-radius: 20px !important;
  overflow: hidden;
}

.production-date-range {
  :deep(.el-range-input),
  :deep(.el-range-separator),
  :deep(.el-range__close-icon),
  :deep(.el-range__icon) {
    background-color: transparent;
  }
}

.el-card.production-dashboard-card {
  height: 100%;

  .el-card__body {
    padding: 0px;
    height: 100%;
  }

  .production-dashboard-card-content {
    display: flex;
    align-items: center;
    height: 100%;

    h2 {
      margin-top: 0;
      font-size: 1.4em;
      font-weight: bold;
    }

    .dashboard-icon {
      height: 100%;
      width: 70px;
      min-width: 70px;
      padding: 5px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      color: #ffffff;

      svg {
        width: 38px;
        height: 38px;
        flex-shrink: 0;
      }

      i {
        padding: 15px;
        font-size: 45px;
        margin: auto;
      }

      &.project {
        background-color: #F9156F;
      }

      &.employee {
        background-color: #4AABC3;
      }

      &.holidays-absences {
        background-color: #474CE6;
      }
    }

    .dashboard-text {
      width: 100%;
      display: flex;
      height: 100%;
      flex-direction: column;
      padding: 10px;
      gap: 5px
    }

    .h6-text {
      font-size: 12px;
    }
  }
}

@media (min-width: 1200px) and (max-width: 1439px) {
  .production-summary-row {
    .production-summary-col {
      max-width: 50%;
      flex: 0 0 50%;
    }
  }

  .production-users-row {
    .production-user-col {
      max-width: 33.33333333%;
      flex: 0 0 33.33333333%;
    }
  }
}

@media (max-width: 1440px) {
  .el-card.production-dashboard-card {
    .production-dashboard-card-content {
      min-height: 124px;

      h2 {
        font-size: 1.2em;
        line-height: 1.2;
      }

      .dashboard-icon {
        width: 62px;
        min-width: 62px;

        svg {
          width: 32px;
          height: 32px;
        }
      }

      .dashboard-text {
        padding: 8px 10px;
      }

      .h6-text,
      small {
        font-size: 11px;
        line-height: 1.35;
      }
    }
  }

  .production-filters-row {
    row-gap: 10px;
  }
}

@media (max-width: 1200px) {
  .refresh-col-wrapper {
    padding-left: 0;
  }
}

@media (max-width: 360px) {
  .el-card.production-dashboard-card {
    .production-dashboard-card-content {
      .dashboard-icon {
        width: 56px;
        min-width: 56px;

        svg {
          width: 30px;
          height: 30px;
        }
      }
    }
  }
}
@media (max-width: 320px) {
  .el-card.production-dashboard-card {
    .production-dashboard-card-content {
      .dashboard-icon {
        width: 40px;
        min-width: 40px;

        svg {
          width: 24px;
          height: 24px;
        }
      }
    }
  }
}
</style>
